import { useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import {
  EDITOR_COLOR_SCHEMES,
  type EditorColorSchemeId,
  type EditorColorSchemeTokenColors,
} from '../../../../core/theme/monacoColorSchemes';
import { CUSTOM_COLOR_PICKERS } from '../../../../core/theme/customEditorColors';
import { CustomColorPickerRow } from './CustomColorPickerRow';

interface CustomEditorColorsPanelProps {
  /** The variant being edited — set by the dialog's "Edit variant:" toggle (see `CustomEditorColorsSection`). */
  variant: 'light' | 'dark';
  /** This variant's DRAFT token table — the caller (`EditorColorSchemeDialog`) owns the draft. */
  colors: EditorColorSchemeTokenColors;
  /** Commits `hex` to every token in `tokens`, for `variant`, in the draft. Never writes the store. */
  onColorChange: (tokens: readonly (keyof EditorColorSchemeTokenColors)[], hex: string) => void;
  /** Re-seeds BOTH draft variants from `scheme`'s preset tables. Never writes the store. */
  onCopyFrom: (scheme: EditorColorSchemeId) => void;
}

/**
 * Editor colors dialog → Custom section (staged-draft dialog iteration): a
 * bordered card with a "copy from preset" shortcut and one color picker row
 * per token group, for whichever variant `variant` names — fully controlled,
 * every edit flows into the dialog's draft via `onColorChange`/`onCopyFrom`.
 */
export function CustomEditorColorsPanel({ variant, colors, onColorChange, onCopyFrom }: CustomEditorColorsPanelProps) {
  const { track } = useAnalytics();
  // Controlled-then-reset so the "Copy from…" select reads as an action
  // (a button, not a persistent choice) — re-picking the same scheme still
  // applies, since the value is always '' right before the pick.
  const [copyFromValue, setCopyFromValue] = useState('');

  return (
    <div
      className="space-y-3 rounded-md border border-border bg-background p-4"
      data-testid="custom-editor-colors-panel"
    >
      <div className="flex justify-end">
        <Select
          value={copyFromValue}
          onValueChange={(value) => {
            const scheme = value as EditorColorSchemeId;
            onCopyFrom(scheme);
            track('feature:editor_custom_colors_copied', { from: scheme });
            setCopyFromValue('');
          }}
        >
          <SelectTrigger size="sm" className="w-[150px]" data-testid="custom-colors-copy-from">
            <SelectValue placeholder="Copy from…" />
          </SelectTrigger>
          <SelectContent>
            {EDITOR_COLOR_SCHEMES.map((scheme) => (
              <SelectItem key={scheme.id} value={scheme.id} data-testid={`custom-colors-copy-from-option-${scheme.id}`}>
                {scheme.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
        {CUSTOM_COLOR_PICKERS.map((picker) => (
          <CustomColorPickerRow
            key={picker.id}
            picker={picker}
            value={colors[picker.tokens[0]]}
            variant={variant}
            onChange={(hex) => onColorChange(picker.tokens, hex)}
          />
        ))}
      </div>
    </div>
  );
}
